// Generated from legacy db.ts to keep files under 500 lines.
import { sql, sqlRaw } from "../../base";
import type { TaskCompletionStatistics } from "../../types";

const taskCompletionStatisticsModule = {
  async recordCompletion(params: {
    userId: string;
    taskId: string;
    taskTitle: string;
    taskDescription?: string | null;
    priority?: string | null;
    minutesSpent: number;
  }): Promise<void> {
    const minutesSpent = Math.max(0, Math.round(params.minutesSpent || 0));
    if (minutesSpent <= 0) {
      return;
    }

    let goalTemplateId: string | null = null;
    try {
      const templateMatch = await sql`
        SELECT tt.goal_template_id
        FROM task_templates tt
        JOIN goal_templates gt ON tt.goal_template_id = gt.id
        WHERE LOWER(TRIM(tt.title)) = LOWER(TRIM(${params.taskTitle}))
          AND LOWER(COALESCE(tt.description, '')) = LOWER(COALESCE(${params.taskDescription || ''}, ''))
          AND COALESCE(tt.priority, 'Medium') = COALESCE(${params.priority || 'Medium'}, 'Medium')
        ORDER BY CASE WHEN gt.user_id = ${params.userId} THEN 0 ELSE 1 END,
                 CASE WHEN gt.is_system THEN 1 ELSE 0 END
        LIMIT 1
      `;
      if (templateMatch?.length) {
        goalTemplateId = templateMatch[0].goal_template_id;
      }
    } catch (error) {
      console.error('Error locating template for task statistics:', error);
    }

    // The unique constraint idx_task_stats_unique_key is created during migrations,
    // so this upsert can run safely without issuing DDL from the browser.
    await sqlRaw(
      `INSERT INTO task_completion_statistics (
         user_id, task_title, task_description, goal_template_id,
         completion_count, total_minutes_spent, avg_minutes_per_completion,
         min_minutes_completion, max_minutes_completion, last_completion_minutes,
         first_completion_date, last_completion_date, created_at, updated_at
       ) VALUES (
         $1, $2, $3, $4,
         1, $5, $5,
         $5, $5, $5,
         CURRENT_TIMESTAMP, CURRENT_TIMESTAMP, CURRENT_TIMESTAMP, CURRENT_TIMESTAMP
       )
       ON CONFLICT (user_id, task_title, task_description, goal_template_id)
       DO UPDATE SET
         completion_count = completion_count + 1,
         total_minutes_spent = total_minutes_spent + $5,
         avg_minutes_per_completion = CAST(total_minutes_spent + $5 AS REAL) / (completion_count + 1),
         min_minutes_completion = MIN(COALESCE(min_minutes_completion, $5), $5),
         max_minutes_completion = MAX(COALESCE(max_minutes_completion, $5), $5),
         last_completion_minutes = $5,
         last_completion_date = CURRENT_TIMESTAMP,
         updated_at = CURRENT_TIMESTAMP`,
      [params.userId, params.taskTitle, params.taskDescription ?? null, goalTemplateId, minutesSpent]
    );
  },

  async getEstimate(title: string, description: string | null, userId: string, goalTemplateId?: string): Promise<TaskCompletionStatistics | null> {
    try {
      // First try to get exact match with template ID if provided
      if (goalTemplateId) {
        const result = await sql`
          SELECT * FROM task_completion_statistics
          WHERE user_id = ${userId}
          AND task_title = ${title}
          AND COALESCE(task_description, '') = ${description || ''}
          AND goal_template_id = ${goalTemplateId}
          LIMIT 1
        `;
        if (result[0]) return result[0] as TaskCompletionStatistics;
      }
      
      // Fall back to match without template ID
      const result = await sql`
        SELECT * FROM task_completion_statistics
        WHERE user_id = ${userId}
        AND task_title = ${title}
        AND COALESCE(task_description, '') = ${description || ''}
        AND goal_template_id IS NULL
        LIMIT 1
      `;
      return result[0] as TaskCompletionStatistics || null;
    } catch (error) {
      console.error('Error getting task estimate:', error);
      return null;
    }
  },
  
  async getEstimateForRegularTask(title: string, description: string | null, frequency: string, priority: string, userId: string): Promise<number | null> {
    try {
      // For regular tasks, use the regular_task_statistics view
      const result = await sql`
        SELECT avg_minutes_per_completion
        FROM regular_task_statistics
        WHERE user_id = ${userId}
        AND title = ${title}
        AND frequency = ${frequency}
        AND description = ${description || ''}
        LIMIT 1
      `;
      
      if (result[0]?.avg_minutes_per_completion) {
        return Math.round(result[0].avg_minutes_per_completion);
      }
      
      // Fallback to task_completion_statistics for backwards compatibility
      const fallbackResult = await sql`
        SELECT avg_minutes_per_completion
        FROM task_completion_statistics
        WHERE user_id = ${userId}
        AND task_title = ${title}
        AND COALESCE(task_description, '') = ${description || ''}
        LIMIT 1
      `;
      return fallbackResult[0]?.avg_minutes_per_completion || null;
    } catch (error) {
      console.error('Error getting regular task estimate:', error);
      return null;
    }
  },
  
  async getBatchEstimates(tasks: Array<{title: string, description?: string | null, goalTemplateId?: string}>, userId: string): Promise<Map<string, number>> {
    try {
      if (tasks.length === 0) return new Map();
      
      // Create unique task identifiers
      const taskIdentifiers = tasks.map(t => ({
        title: t.title,
        description: t.description || '',
        goalTemplateId: t.goalTemplateId || null
      }));
      
      // Query for all matching statistics
      // Fetch all stats for the provided titles; filter in JS for description match
      const titles = [...new Set(tasks.map(t => t.title))];
      const placeholders = titles.map((_, i) => `$${i + 1}`).join(', ');
      const rows = await sqlRaw(
        `SELECT task_title, COALESCE(task_description, '') as description, avg_minutes_per_completion, goal_template_id
         FROM task_completion_statistics
         WHERE user_id = $${titles.length + 1}
         AND task_title IN (${placeholders})`,
        [...titles, userId]
      );

      const estimateMap = new Map<string, number>();
      for (const t of taskIdentifiers) {
        const desc = t.description || '';
        const match = rows.find((r: any) => r.task_title === t.title && String(r.description || '') === desc);
        if (match?.avg_minutes_per_completion) {
          estimateMap.set(`${t.title}|${desc}`, Math.round(match.avg_minutes_per_completion));
        }
      }
      return estimateMap;
    } catch (error) {
      console.error('Error getting batch estimates:', error);
      return new Map();
    }
  }
};

export type TaskCompletionStatisticsModule = typeof taskCompletionStatisticsModule;

export function registerTaskCompletionStatistics(db: { taskCompletionStatistics?: TaskCompletionStatisticsModule }) {
  db.taskCompletionStatistics = taskCompletionStatisticsModule;
}

export { taskCompletionStatisticsModule as taskCompletionStatistics };
